import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaFire, FaArrowUp, FaArrowDown, FaCoins, FaGift } from 'react-icons/fa';
import { TOAST_TYPES } from '../utils/toastNotifications';
import { useToast } from '../hooks/useToast';
import { trackEvent } from '../utils/analytics';
import useReducedMotion from '../hooks/useReducedMotion';

const TOAST_ICONS = {
  top_match: FaFire,
  accumulator_prompt: FaCoins,
  new_bonus: FaGift,
};

const TOAST_VARIANTS = {
  primary: {
    progress: 'bg-primary/40',
    button:
      'border border-primary/30 text-primary hover:bg-primary/10 dark:hover:bg-primary/20',
  },
  success: {
    progress: 'bg-success/40',
    button:
      'border border-success/30 text-success-dark dark:text-success-bright hover:bg-success-light/80 dark:hover:bg-success/20',
  },
  warning: {
    progress: 'bg-warning/40',
    button:
      'border border-warning/30 text-warning-dark dark:text-warning-bright hover:bg-warning-light/80 dark:hover:bg-warning/20',
  },
  info: {
    progress: 'bg-info/40',
    button: 'border border-info/30 text-info-dark dark:text-info-bright hover:bg-info-light/80 dark:hover:bg-info/20',
  },
};

const Toast = () => {
  const { currentToast, isVisible, dismiss } = useToast();
  const reduceMotion = useReducedMotion();

  if (!currentToast || typeof currentToast !== 'object') return null;
  if (!currentToast.type || typeof currentToast.type !== 'string') return null;

  const toastConfig = TOAST_TYPES[currentToast.type.toUpperCase()];
  if (!toastConfig) return null;

  const variant = TOAST_VARIANTS[toastConfig.color] || TOAST_VARIANTS.primary;
  const ToastIcon = currentToast.direction === 'up'
    ? FaArrowUp
    : currentToast.direction === 'down'
      ? FaArrowDown
      : TOAST_ICONS[currentToast.type];

  const motionProps = reduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.15 },
      }
    : {
        initial: { opacity: 0, y: 24, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 24, scale: 0.98 },
        transition: { type: 'spring', stiffness: 300, damping: 25 },
      };

  const actionClassName = `
    ${variant.button}
    shrink-0 min-h-[44px] px-3 rounded-lg font-semibold text-xs sm:text-sm
    transition-colors duration-200
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50
    inline-flex items-center gap-1
    ${reduceMotion ? '' : 'active:scale-95'}
  `;

  return (
    <AnimatePresence>
      {isVisible && (
        // Above BottomNav on mobile so the sticky header stays clickable; bottom-right on lg
        <motion.div
          {...motionProps}
          className="fixed inset-x-4 bottom-above-nav mb-2 z-[70] lg:inset-x-auto lg:right-6 lg:w-96"
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            className={`
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              border border-gray-100 dark:border-gray-700
              rounded-2xl shadow-modal dark:shadow-modal-dark
              pl-3 pr-1 py-1 relative overflow-hidden w-full
              flex items-center gap-2
            `}
          >
            {ToastIcon ? <ToastIcon className="w-4 h-4 shrink-0 text-primary" aria-hidden="true" /> : null}

            <div className="min-w-0 flex-1 py-1.5">
              <h3 className="font-bold text-sm leading-tight truncate">{currentToast.title}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-snug truncate">
                {currentToast.message}
              </p>
            </div>

            {currentToast.action ? (
              currentToast.action.url ? (
                <Link
                  to={currentToast.action.url}
                  onClick={() => {
                    trackEvent('click-toast');
                    dismiss();
                  }}
                  className={actionClassName}
                >
                  <span className="truncate">{currentToast.action.text}</span>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    trackEvent('click-toast');
                    if (currentToast.action.onClick) {
                      currentToast.action.onClick();
                    }
                    dismiss();
                  }}
                  className={actionClassName}
                >
                  <span className="truncate">{currentToast.action.text}</span>
                </button>
              )
            ) : null}

            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              aria-label="Dismiss notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <motion.div
              className={`absolute bottom-0 left-0 h-0.5 ${variant.progress}`}
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{
                duration: toastConfig.duration / 1000,
                ease: 'linear',
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
